import apiClient from './api-client';
import logger from '../shared/utils/logger';
import { 
  PaginatedResponse,
  UpdateListResponseDto,
  UpdateDetailResponseDto,
  UpdateCreateDto,
  UpdateFilters
} from '../types/dtos';

/**
 * Update service for managing application updates
 */
class UpdateService {
  /**
   * Check for updates
   * @param appId The application ID
   * @param version The current version
   * @param platform The platform (ios, android, web)
   * @returns Promise with update information
   */
  public async checkForUpdates(
    appId: string = 'com.applepie.app',
    version: string = '1.0.0',
    platform: string = 'web'
  ): Promise<UpdateDetailResponseDto | null> {
    try {
      const params = new URLSearchParams();
      params.append('appId', appId);
      params.append('version', version);
      params.append('platform', platform);
      
      const response = await apiClient.get<UpdateDetailResponseDto | null>(`/update?${params.toString()}`);
      return response;
    } catch (error: unknown) {
      logger.error('Error checking for updates:', error);
      return null;
    }
  }

  /**
   * Get all updates (admin only)
   * @param filters Optional filters
   * @returns Promise with the update list
   */
  public async getAllUpdates(filters: UpdateFilters = {}): Promise<UpdateListResponseDto[]> {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      if (filters.appId) {
        params.append('appId', filters.appId);
      }
      
      if (filters.platform) {
        params.append('platform', filters.platform);
      }
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      return await apiClient.get<UpdateListResponseDto[]>(`/admin/updates${queryString}`);
    } catch (error: unknown) {
      logger.error('Error fetching updates:', error);
      throw error;
    }
  }

  /**
   * Get paginated updates with optional filtering and sorting (admin only)
   * @param page The page number (1-indexed)
   * @param limit The number of records per page
   * @param filters Optional filters and search parameters
   * @returns Promise with paginated updates
   */
  public async getUpdatesPaginated(
    page: number = 1, 
    limit: number = 10, 
    filters: UpdateFilters = {}
  ): Promise<PaginatedResponse<UpdateListResponseDto>> {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      // Add optional filters
      if (filters.search) {
        params.append('search', filters.search);
      }
      
      if (filters.platform) {
        params.append('platform', filters.platform);
      }
      
      if (filters.appId) {
        params.append('appId', filters.appId);
      }
      
      if (filters.sortField) {
        params.append('sortField', filters.sortField);
        params.append('sortDirection', filters.sortDirection || 'ASC');
      }
      
      return await apiClient.get<PaginatedResponse<UpdateListResponseDto>>(`/admin/updates/paginated?${params.toString()}`);
    } catch (error: unknown) {
      logger.error('Error fetching paginated updates:', error);
      throw error;
    }
  }

  /**
   * Create a new update (admin only)
   * @param updateData The update data including the package file
   * @returns Promise with the created update
   */
  public async createUpdate(updateData: UpdateCreateDto): Promise<UpdateDetailResponseDto> {
    try {
      // Create form data for file upload
      const formData = new FormData();
      formData.append('appId', updateData.appId);
      formData.append('version', updateData.version);
      formData.append('platform', updateData.platform);
      formData.append('mandatory', updateData.isMandatory ? '1' : '0');
      formData.append('releaseNotes', updateData.releaseNotes);
      formData.append('package', updateData.package);
      
      return await apiClient.postFormData<UpdateDetailResponseDto>('/admin/updates', formData);
    } catch (error: unknown) {
      logger.error('Error creating update:', error);
      throw error;
    }
  }

  /**
   * Delete an update (admin only)
   * @param id The update ID
   * @returns Promise with the response
   */
  public async deleteUpdate(id: number): Promise<void> {
    try {
      return await apiClient.delete<void>(`/admin/updates/${id}`);
    } catch (error: unknown) {
      logger.error(`Error deleting update ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get download URL for an update package
   * @param version The version
   * @param platform The platform
   * @param includeZip Whether to include .zip extension
   * @returns The download URL
   */
  public getDownloadUrl(version: string, platform: string, includeZip: boolean = false): string {
    const baseUrl = apiClient.getBaseUrl();
    const url = `${baseUrl}/updates/${platform}`;
    return includeZip ? `${url}/${version}.zip` : url;
  }

  public getBaseUrl(): string {
    return apiClient.getBaseUrl();
  }
}

// Create and export a singleton instance
const updateService = new UpdateService();
export default updateService;

